import { useState, useEffect } from "react";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";

type Company = {
  id: number;
  name: string;
  sites: { id: number; name: string }[];
};

export default function ChangeCompanyModal({
  isOpen,
  onClose,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (companyId: number, siteId: number) => void;
}) {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | string>(0);
  const [selectedSiteId, setSelectedSiteId] = useState<number | string>(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && (!companies || companies.length === 0)) {
      fetchCompanies();
    }
  }, [isOpen]);

  async function fetchCompanies() {
    setIsLoading(true);
    try {
      const resp = await RightTimeApi.getAsync("/CompaniesSites");
      if (resp && resp.ok) {
        setCompanies(resp.jsonData.companies);
      } else {
        console.error("Error fetching companies:", resp?.statusText);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    } finally {
      setIsLoading(false);
    }
  }

  function handleCompanyChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setSelectedCompanyId(Number(e.target.value));
    setSelectedSiteId(0); // Reset site when company changes
  }

  function handleSiteChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setSelectedSiteId(Number(e.target.value));
  }

  function handleConfirm() {
    if (selectedCompanyId && selectedSiteId) {
      onConfirm(Number(selectedCompanyId), Number(selectedSiteId));
      handleClose();
    }
  }

  function handleClose() {
    setSelectedCompanyId(0);
    setSelectedSiteId(0);
    onClose();
  }

  const selectedSites =
    companies.find((c) => c.id === selectedCompanyId)?.sites || [];
  const isConfirmDisabled = !selectedCompanyId || !selectedSiteId || isLoading;

  const modalFooter = (
    <>
      <button className="btn btn-secondary" onClick={handleClose}>
        Cancel
      </button>
      <button
        className="btn btn-primary"
        onClick={handleConfirm}
        disabled={isConfirmDisabled}
      >
        Confirm
      </button>
    </>
  );

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={handleClose} 
      title="Change Company & Site" 
      size="sm"
      className="changeCompanyModal"
      footer={modalFooter}
    >
      <div className="changeCompanyModalContent">
        {isLoading ? (
          <div className="loading-message">Loading companies...</div>
        ) : (
          <>
            <div className="formGroup">
              <label htmlFor="companySelect">Company:</label>
              <select
                id="companySelect"
                value={selectedCompanyId}
                onChange={handleCompanyChange}
                className="companySelect"
              >
                <option value={0}>Select Company</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            {selectedCompanyId && (
              <div className="formGroup">
                <label htmlFor="siteSelect">Site:</label>
                <select
                  id="siteSelect"
                  value={selectedSiteId}
                  onChange={handleSiteChange}
                  className="siteSelect"
                >
                  <option value={0}>Select Site</option>
                  {selectedSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
